import React, { useEffect, useState } from "react";
import { useMsal } from "@azure/msal-react";
import { InteractionRequiredAuthError } from "@azure/msal-browser";
import { isTestEnv } from "../globals";
import { useUser } from "../contexts/UserContext";
import { loginRequest, protectedResources } from "../authConfig";
import NotFoundPage from "../pages/ErrorPage";
import { Box, CircularProgress } from "@mui/material";
import useFetchWithMsal from "../hooks/useFetchWithMsal";

// Some routes sit under the Managers Dashboard and need a second, narrower
// check beyond the regular role permission: the specific sub-dashboard key
// must be present in the user's managerDashboard allowedDashboards (or the
// user must be a manager-dashboard admin). Pass `requiredDashboardKey` for
// those routes — see backend/config/managerDashboards.js for valid keys.
const ProtectedRoute = ({ children, requiredPermission, requiredDashboardKey }) => {
  isTestEnv && console.log("ProtectedRoute");

  const { instance } = useMsal();
  const { user } = useUser();
  const { execute } = useFetchWithMsal({ scopes: protectedResources.apiScopes.scopes.read });
  const [isValidating, setIsValidating] = useState(true);
  const [hasValidToken, setHasValidToken] = useState(false);
  const [isCheckingDashboardAccess, setIsCheckingDashboardAccess] = useState(!!requiredDashboardKey);
  const [hasDashboardAccess, setHasDashboardAccess] = useState(false);

  const activeAccount = instance.getActiveAccount();
  isTestEnv && console.log("activeAccount", activeAccount);
  isTestEnv && console.log("user.accessRights", user);

  useEffect(() => {
    const validateToken = async () => {
      if (!activeAccount) {
        setIsValidating(false);
        return;
      }

      try {
        await instance.initialize();
        
        try {
          await instance.acquireTokenSilent({
            ...loginRequest,
            account: activeAccount,
          });
          setHasValidToken(true);
        } catch (silentError) {
          if (silentError instanceof InteractionRequiredAuthError) {
            isTestEnv && console.log("Token expired, redirecting to login");
            await instance.acquireTokenRedirect({
              ...loginRequest,
              account: activeAccount,
            });
            return;
          }
          throw silentError;
        }
      } catch (error) {
        console.error("Token validation error:", error);
        setHasValidToken(false);
      } finally {
        setIsValidating(false);
      }
    };

    validateToken();
  }, [activeAccount, instance]);

  useEffect(() => {
    if (!requiredDashboardKey || isValidating) return;

    if (!hasValidToken || user.accessRights?.admin) {
      setHasDashboardAccess(!!user.accessRights?.admin);
      setIsCheckingDashboardAccess(false);
      return;
    }

    const checkDashboardAccess = async () => {
      try {
        const result = await execute(
          "GET",
          `${process.env.REACT_APP_BACKEND_BASE_URL}/api/managerDashboard/myAccess`
        );
        setHasDashboardAccess(
          !!result?.isAdmin || !!result?.allowedDashboards?.includes(requiredDashboardKey)
        );
      } catch (err) {
        setHasDashboardAccess(false);
      } finally {
        setIsCheckingDashboardAccess(false);
      }
    };

    checkDashboardAccess();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requiredDashboardKey, isValidating, hasValidToken, user.accessRights?.admin]);

  if (isValidating || isCheckingDashboardAccess) {
    return (
      <Box 
        display="flex" 
        alignItems="center" 
        justifyContent="center" 
        minHeight="200px"
      >
        <CircularProgress size={40} />
      </Box>
    );
  }

  if (!hasValidToken || !activeAccount) {
    return <NotFoundPage />;
  }

  if (user.accessRights !== undefined &&
      (!user.accessRights.admin && !user.accessRights?.[requiredPermission])) {
    return <NotFoundPage />;
  }

  if (requiredDashboardKey && !hasDashboardAccess) {
    return <NotFoundPage />;
  }

  return children;
};

export default ProtectedRoute;